import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Lock, Check, ArrowRight, Search, Star, Clock, TrendingUp, Award } from "lucide-react";
import { toast } from "sonner";
import api, { resolveAvatar } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import TraitDots from "@/components/TraitDots";
const TIER_LABEL = { starter: "Starter", pro: "Pro", business: "Business", enterprise: "Enterprise", lifetime_free: "Lifetime Free", owner_unlimited: "Owner" };
const TIER_ORDER = ["starter", "pro", "business", "enterprise", "lifetime_free", "owner_unlimited"];
const UNLIMITED_PLANS = new Set(["lifetime_free", "owner_unlimited"]);
const tierAllowed = (companyTier, workerTier) => {
    if (UNLIMITED_PLANS.has(companyTier)) return true;
    return TIER_ORDER.indexOf(companyTier) >= TIER_ORDER.indexOf(workerTier);
};

export default function Workforce() {
    const { company } = useAuth();
    const nav = useNavigate();
    const [workers, setWorkers] = useState([]);
    const [hiredSlugs, setHiredSlugs] = useState(new Set());
    const [hiredIdBySlug, setHiredIdBySlug] = useState({});
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all"); // all | functional | available
    const [hiringSlug, setHiringSlug] = useState(null);
    const [roi, setRoi] = useState(null);
    // Sprint I · Step C — Atlas marketplace suggestions
    const [atlasSuggestions, setAtlasSuggestions] = useState({});   // slug → suggestion obj

    const load = async () => {
        const [cat, hired, roiRes, sug] = await Promise.all([
            api.get("/workers/catalog"),
            api.get("/workers/hired"),
            api.get("/workforce/roi").catch(() => ({ data: null })),
            api.get("/atlas/suggestions/marketplace").catch(() => ({ data: { suggestions: [] } })),
        ]);
        setWorkers(cat.data.workers || []);
        const map = {};
        const set = new Set();
        (hired.data.hired || []).forEach((h) => {
            set.add(h.worker_slug);
            map[h.worker_slug] = h.id;
        });
        setHiredSlugs(set);
        setHiredIdBySlug(map);
        setRoi(roiRes.data);
        const sugMap = {};
        (sug.data?.suggestions || []).forEach((s) => { sugMap[s.worker_slug] = s; });
        setAtlasSuggestions(sugMap);
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return workers.filter((w) => {
            if (filter === "functional" && !w.is_functional) return false;
            if (filter === "available" && w.is_functional) return false;
            if (!term) return true;
            return (
                w.name.toLowerCase().includes(term) ||
                w.role.toLowerCase().includes(term) ||
                w.department.toLowerCase().includes(term) ||
                (w.specialization || "").toLowerCase().includes(term)
            );
        });
    }, [workers, q, filter]);

    const hire = async (slug) => {
        setHiringSlug(slug);
        try {
            const { data } = await api.post("/workers/hire", { worker_slug: slug });
            toast.success(`${data.worker.name} joined your team`);
            await load();
            nav(`/app/workers/${data.hired_worker.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Hire failed");
        } finally {
            setHiringSlug(null);
        }
    };

    const openHired = (slug) => nav(`/app/workers/${hiredIdBySlug[slug]}`);

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar p-8">
            <div className="max-w-7xl">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                        <div className="wfos-label">Workforce · Marketplace</div>
                        <h1 className="mt-2 text-3xl font-medium tracking-tight text-gray-900">
                            Not another prompt window. A complete Digital Workforce for every department.
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Hire, manage, and scale your digital workforce — {workers.length} Workforce Members ready, {workers.filter((w) => w.is_functional).length} activated today.
                        </p>
                    </div>
                    <Link
                        to="/app/workforce/hired"
                        data-testid="link-view-hired"
                        className="inline-flex items-center gap-1 text-sm font-medium text-[#16a34a] hover:text-[#15803d]"
                    >
                        View your team ({hiredSlugs.size}) <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </Link>
                </div>

                {/* ROI pill (Phase 1) */}
                {roi && (roi.hired_count > 0 || roi.recommended_next_worker_slug) && (
                    <div
                        data-testid="workforce-roi-pill"
                        className="mt-6 wfos-card p-4 flex flex-wrap items-center gap-4 bg-[#f0fdf4] border-[#bbf7d0]"
                    >
                        <div className="inline-flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                            <span className="wfos-label text-[#16a34a]">Projected ROI</span>
                        </div>
                        <div className="text-[15px] text-gray-900">
                            <b className="tabular-nums text-lg">${(roi.hired_savings_monthly || 0).toLocaleString()}</b>
                            <span className="text-gray-500">/mo saved from your current team</span>
                        </div>
                        {roi.recommended_next_worker_slug && (
                            <div className="text-[13px] text-gray-700">
                                · Add <b>{roi.recommended_next_worker_name}</b> for an extra <b className="tabular-nums text-[#16a34a]">${(roi.recommended_delta_monthly || 0).toLocaleString()}/mo</b>
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="workforce-search"
                            placeholder="Search by name, role, department, or specialization…"
                            className="w-full h-10 rounded-lg border border-[#e8e8e3] bg-white pl-9 pr-3 text-sm focus:outline-none focus:border-[#16a34a] focus:ring-2 focus:ring-[#16a34a]/20"
                        />
                    </div>
                    <div className="flex items-center gap-1 rounded-lg border border-[#e8e8e3] bg-white p-1">
                        {[
                            { k: "all", label: `All (${workers.length})` },
                            { k: "functional", label: `Activated (${workers.filter((w) => w.is_functional).length})` },
                            { k: "available", label: `Available (${workers.filter((w) => !w.is_functional).length})` },
                        ].map((t) => (
                            <button
                                key={t.k}
                                onClick={() => setFilter(t.k)}
                                data-testid={`filter-${t.k}`}
                                className={`px-3 h-8 rounded-md text-xs font-medium ${
                                    filter === t.k
                                        ? "bg-[#111827] text-white"
                                        : "text-gray-600 hover:text-gray-900"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filtered.map((w) => {
                        const isHired = hiredSlugs.has(w.slug);
                        const canHire = tierAllowed(company?.plan || "starter", w.tier_required);
                        const rating = w.rating || 4.8;
                        const reviews = w.review_count || 0;
                        const monthly = w.monthly_price ?? 0;
                        const setup = w.setup_fee ?? 0;
                        const savings = w.estimated_monthly_time_savings_hours ?? 0;
                        const years = w.years_experience;
                        return (
                            <div
                                key={w.slug}
                                data-testid={`worker-card-${w.slug}`}
                                className="wfos-card p-5 flex flex-col hover:border-[#d6d6cf] transition-colors relative"
                            >
                                {atlasSuggestions[w.slug] && (
                                    <AtlasSuggestionBadge suggestion={atlasSuggestions[w.slug]} />
                                )}
                                <div className="flex items-start gap-3">
                                    <img
                                        src={resolveAvatar(w.avatar_url)}
                                        alt={w.name}
                                        className="w-14 h-14 rounded-full object-cover border border-[#e8e8e3]"
                                        loading="lazy"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="text-[15px] font-semibold text-gray-900 truncate">{w.name}</div>
                                            {w.is_functional ? (
                                                <span
                                                    data-testid={`functional-badge-${w.slug}`}
                                                    className="wfos-pill wfos-pill--green"
                                                >
                                                    <Sparkles className="w-3 h-3" strokeWidth={2} /> Activated
                                                </span>
                                            ) : (
                                                <span className="wfos-pill">
                                                    <Lock className="w-3 h-3" strokeWidth={2} /> Available
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                                            {w.job_title || w.role}
                                        </div>
                                        <div className="mt-0.5 text-xs text-gray-400">{w.department}</div>
                                        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11px] text-gray-600">
                                            <span className="inline-flex items-center gap-1" data-testid={`worker-rating-${w.slug}`}>
                                                <Star className="w-3 h-3 text-amber-500 fill-amber-500" strokeWidth={1.5} />
                                                <span className="font-medium">{rating.toFixed(1)}</span>
                                                <span className="text-gray-400">({reviews})</span>
                                            </span>
                                            {years !== undefined && years !== null && (
                                                <span className="inline-flex items-center gap-1" data-testid={`worker-years-${w.slug}`}>
                                                    <Award className="w-3 h-3 text-[#16a34a]" strokeWidth={2} />
                                                    <span className="font-medium">{years}y</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <p className="mt-3 text-sm text-gray-600 leading-relaxed line-clamp-2">
                                    {w.tagline}
                                </p>

                                {w.specialization && (
                                    <div className="mt-2 text-[11.5px] text-gray-500 line-clamp-1" data-testid={`worker-spec-${w.slug}`}>
                                        <span className="text-gray-400">Specialization:</span> {w.specialization}
                                    </div>
                                )}

                                {(w.character_traits || []).length > 0 && (
                                    <TraitDots
                                        traits={w.character_traits}
                                        size="xs"
                                        showLabels
                                        className="mt-2"
                                    />
                                )}

                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    <span className="wfos-pill wfos-pill--green" data-testid={`worker-savings-${w.slug}`}>
                                        <Clock className="w-3 h-3" strokeWidth={2} /> Save {savings} hrs/mo
                                    </span>
                                    <span className="wfos-pill text-[10px] px-2 py-0.5">
                                        {TIER_LABEL[w.tier_required]} plan
                                    </span>
                                </div>

                                <div className="mt-5 pt-4 border-t border-[#e8e8e3] flex items-center justify-between">
                                    <div className="min-w-0">
                                        <div className="text-[18px] font-semibold text-gray-900 tabular-nums" data-testid={`worker-price-${w.slug}`}>
                                            ${monthly.toLocaleString()}<span className="text-xs font-normal text-gray-500">/mo</span>
                                        </div>
                                        {setup > 0 && (
                                            <div className="text-[10px] text-gray-400">+ ${setup} setup</div>
                                        )}
                                    </div>
                                    {isHired ? (
                                        <button
                                            onClick={() => openHired(w.slug)}
                                            data-testid={`worker-open-btn-${w.slug}`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-white border border-[#e8e8e3] px-3 h-9 text-xs font-medium text-gray-800 hover:border-[#d6d6cf]"
                                        >
                                            <Check className="w-3.5 h-3.5 text-[#16a34a]" strokeWidth={2} /> Open
                                        </button>
                                    ) : (
                                        <button
                                            disabled={!canHire || hiringSlug === w.slug}
                                            onClick={() => hire(w.slug)}
                                            data-testid={`worker-hire-btn-${w.slug}`}
                                            className="inline-flex items-center gap-1 rounded-lg bg-[#16a34a] px-3.5 h-9 text-xs font-medium text-white hover:bg-[#15803d] disabled:bg-gray-200 disabled:text-gray-400"
                                            title={!canHire ? `Requires ${TIER_LABEL[w.tier_required]} plan` : ""}
                                        >
                                            {hiringSlug === w.slug ? "Activating…" : "Activate"}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {filtered.length === 0 && (
                    <div className="mt-16 text-center text-sm text-gray-500">No workers matched your filter.</div>
                )}
            </div>
        </div>
    );
}


/* ================================================================
   Sprint I · Step C — Atlas suggestion badge
   Rendered on marketplace tiles that appear in the Atlas-suggested
   list. Violet pill with a small dot + "Atlas suggests" label +
   accessible reason on hover / focus / screen-reader.
   ================================================================ */
function AtlasSuggestionBadge({ suggestion }) {
    const [showTip, setShowTip] = useState(false);
    const reason = suggestion?.reason || "Atlas suggests activating this Workforce Member.";
    return (
        <div
            className="absolute top-3 right-3 z-10"
            onMouseEnter={() => setShowTip(true)}
            onMouseLeave={() => setShowTip(false)}
            onFocus={() => setShowTip(true)}
            onBlur={() => setShowTip(false)}
        >
            <button
                type="button"
                data-testid="atlas-suggestion-badge"
                aria-label={`Atlas suggests: ${reason}`}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 h-6 text-[10px] font-semibold uppercase tracking-wider transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                style={{
                    background: "rgba(139,92,246,0.18)",
                    color: "var(--atlas-primary-hover)",
                    border: "1px solid rgba(139,92,246,0.5)",
                    boxShadow: "0 0 12px -2px rgba(139,92,246,0.35), inset 0 1px 0 rgba(255,255,255,0.06)",
                }}
            >
                <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: "var(--atlas-primary)", boxShadow: "0 0 6px var(--atlas-primary-glow)" }}
                />
                Atlas suggests
            </button>
            {showTip && (
                <div
                    role="tooltip"
                    data-testid="atlas-suggestion-tooltip"
                    className="absolute right-0 mt-2 w-64 rounded-lg p-3 z-20"
                    style={{
                        background: "var(--atlas-bg-elevated)",
                        border: "1px solid rgba(139,92,246,0.4)",
                        boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
                        color: "var(--atlas-text-secondary)",
                    }}
                >
                    <div
                        className="text-[10px] font-semibold uppercase tracking-widest mb-1"
                        style={{ color: "var(--atlas-primary-hover)" }}
                    >
                        Atlas Suggests
                    </div>
                    <div className="text-[12.5px] leading-relaxed">{reason}</div>
                </div>
            )}
        </div>
    );
}
